import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextInput, View, Pressable, Text } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useColorScheme } from 'nativewind';
import { useNavigation } from '@react-navigation/native';
import {
  HelpdeskIssueStatusFilter,
  GetAdminHelpdeskIssuesParams,
  Organization,
  Pagination as IPagination,
  Topic,
} from '../../client/interfaces';
import useDebounce from '../../hooks/useDebounce';
import svgIcons from '../../assets';
import { DropdownCheckbox, Icon, RadioGroup, ResultCounter } from '../index';
import { cln } from '../../utils/classnames';
import { useResize } from '../../utils/resize';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { isNative, isTablet, isWeb } from '../../utils/responsive';

interface Props {
  params: GetAdminHelpdeskIssuesParams;
  paramSetter: React.Dispatch<React.SetStateAction<GetAdminHelpdeskIssuesParams>>;
  pagination?: IPagination;
  showStatus?: boolean;
  totalCount?: number;
  clearOnBlur?: boolean;
  topics: Topic[];
  organizations: Organization[];
}

const HelpdeskIssueFilter: React.FC<Props> = ({
  params,
  paramSetter,
  pagination,
  showStatus = true,
  totalCount,
  clearOnBlur = true,
  topics,
  organizations,
}) => {
  const navigation = useNavigation();
  const { t } = useTranslation();
  const { colorScheme } = useColorScheme();
  const { isWidthBelowMedium } = useResize();

  // Search input
  const [search, setSearch] = useState<string>(params?.search || '');
  const [searchActive, setSearchActive] = useState(false);
  const debouncedSearch = useDebounce(search);
  const searchInputRef = useRef(null);

  // Sync local search state when params.search changes externally (e.g., filter reset)
  useEffect(() => {
    if (params?.search !== search && params?.search === '') {
      setSearch('');
    }
  }, [params?.search]);

  // Status
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    HelpdeskIssueStatusFilter.all,
  );

  // Topics dropdown
  const [topicSearch, setTopicSearch] = useState<string>('');
  const topicInputRef = useRef(null);
  const {
    visible: topicsVisible,
    setVisible: setTopicsVisible,
    reference: topicsRef,
  } = useOnClickOutside(false);

  // Organizations dropdown
  const [orgSearch, setOrgSearch] = useState<string>('');
  const orgInputRef = useRef(null);
  const {
    visible: orgsVisible,
    setVisible: setOrgsVisible,
    reference: orgsRef,
  } = useOnClickOutside(false);

  // Filtered and sorted topics (checked items on top)
  const filteredTopics = useMemo(() => {
    let filtered = topics;
    if (topicSearch) {
      filtered = topics.filter((topic) =>
        topic.translationValue?.toLowerCase().includes(topicSearch.toLowerCase()),
      );
    }
    return [...filtered].sort((a, b) => {
      const aSelected = params.topics?.some((t) => t.id === a.id);
      const bSelected = params.topics?.some((t) => t.id === b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  }, [topics, topicSearch, params.topics]);

  // Filtered and sorted organizations (checked items on top)
  const filteredOrganizations = useMemo(() => {
    let filtered = organizations;
    if (orgSearch) {
      filtered = organizations.filter((org) =>
        org.name?.toLowerCase().includes(orgSearch.toLowerCase()),
      );
    }
    return [...filtered].sort((a, b) => {
      const aSelected = params.organizations?.some((o) => o.id === a.id);
      const bSelected = params.organizations?.some((o) => o.id === b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  }, [organizations, orgSearch, params.organizations]);

  // Toggle topic selection
  const toggleTopic = (topic: Topic) => {
    paramSetter((prev) => ({
      ...prev,
      page: 1,
      topics: prev.topics?.some((t) => t.id === topic.id)
        ? prev.topics.filter((t) => t.id !== topic.id)
        : [...(prev.topics || []), topic],
    }));
  };

  // Toggle organization selection
  const toggleOrganization = (org: Organization) => {
    paramSetter((prev) => ({
      ...prev,
      page: 1,
      organizations: prev.organizations?.some((o) => o.id === org.id)
        ? prev.organizations.filter((o) => o.id !== org.id)
        : [...(prev.organizations || []), org],
    }));
  };

  // Clear all filters
  function emptyFilters() {
    setSearch('');
    setTopicSearch('');
    setOrgSearch('');
    setTopicsVisible(false);
    setOrgsVisible(false);
    setSelectedStatus(HelpdeskIssueStatusFilter.all);
    paramSetter((prev) => ({
      ...prev,
      page: 1,
      search: '',
      topics: [],
      organizations: [],
      status: HelpdeskIssueStatusFilter.all,
    }));
  }

  // Clear filters on navigation blur
  useEffect(() => {
    if (!clearOnBlur) return;
    const unsubscribe = navigation.addListener('blur', () => {
      emptyFilters();
    });
    return unsubscribe;
  }, [navigation, clearOnBlur]);

  // Update search param with debounce
  useEffect(() => {
    if (params.search !== debouncedSearch) {
      paramSetter((prev) => ({ ...prev, page: 1, search: debouncedSearch }));
    }
  }, [debouncedSearch]);

  // Update status param
  useEffect(() => {
    const newStatus =
      selectedStatus === HelpdeskIssueStatusFilter.all
        ? undefined
        : (selectedStatus as HelpdeskIssueStatusFilter);
    if (params.status !== newStatus) {
      paramSetter((prev) => ({
        ...prev,
        page: 1,
        status: newStatus,
      }));
    }
  }, [selectedStatus]);

  // Placeholder colors
  const getPlaceholderColor = (isActive: boolean) => {
    if (!isActive) {
      return colorScheme === 'dark' ? '#888888' : '#101010';
    }
    return colorScheme === 'dark' ? '#101010' : '#888888';
  };

  return (
    <View className="mb-2 md:mb-4 z-50">
      <View className="flex-column" style={{ columnGap: 16 }}>
        {/* Row with Search, Topics, Organizations */}
        <View
          className="flex-column sm:flex-row relative z-30"
          style={{ columnGap: 16, rowGap: 16 }}
        >
          {/* Search Input */}
          <View className={cln('flex-row items-center h-12', (isWeb || isTablet) && 'flex-1')}>
            <View className="absolute ml-4 z-20">
              <Icon
                icon={svgIcons.searchIcon}
                mobileSize={{ width: 24, height: 24 }}
                webSize={{ width: 24, height: 24 }}
                inverted={searchActive}
              />
            </View>
            <TextInput
              ref={searchInputRef}
              placeholderTextColor={getPlaceholderColor(searchActive)}
              className={cln(
                searchActive
                  ? 'text-neutral-50 bg-neutral-950 dark:text-neutral-950 dark:bg-neutral-50'
                  : 'text-neutral-950 bg-neutral-50 dark:text-neutral-50 dark:bg-neutral-950',
                'h-12 border-[1px] flex-1 pl-[52px] pr-3 border-neutral-900 dark:border-neutral-200',
              )}
              focusable={false}
              onChangeText={setSearch}
              onFocus={() => {
                setSearchActive(true);
                setTopicsVisible(false);
                setOrgsVisible(false);
              }}
              onBlur={() => setSearchActive(false)}
              value={search}
              keyboardType="ascii-capable"
              placeholder={t('common:search')}
              blurOnSubmit={true}
              onSubmitEditing={() => setSearchActive(false)}
            />
          </View>

          {/* Topics Dropdown */}
          <DropdownCheckbox
            testID="helpdesk-topics-filter"
            mobileDropdownTestId="helpdesk-topics-filter-mobile"
            containerRef={isNative ? undefined : topicsRef}
            inputRef={topicInputRef}
            dropdownVisible={topicsVisible}
            onFocus={() => {
              setTopicsVisible(true);
              setOrgsVisible(false);
              setSearchActive(false);
            }}
            onChangeText={setTopicSearch}
            inputValue={topicSearch}
            placeholder={t('admin:table_columns:common:topics')}
            selectedItems={params.topics}
            inputOnPress={() => {
              setTopicsVisible(!topicsVisible);
              setOrgsVisible(false);
            }}
            onClose={() => setTopicsVisible(false)}
            filteredContent={filteredTopics}
            itemOnPress={(item) => toggleTopic(item as Topic)}
            zIndex={'z-20'}
          />

          {/* Organizations Dropdown */}
          <DropdownCheckbox
            testID="helpdesk-departments-filter"
            mobileDropdownTestId="helpdesk-departments-filter-mobile"
            containerRef={isNative ? undefined : orgsRef}
            inputRef={orgInputRef}
            dropdownVisible={orgsVisible}
            onFocus={() => {
              setOrgsVisible(true);
              setTopicsVisible(false);
              setSearchActive(false);
            }}
            onChangeText={setOrgSearch}
            inputValue={orgSearch}
            placeholder={t('admin:table_columns:common:departments')}
            selectedItems={params.organizations}
            inputOnPress={() => {
              setOrgsVisible(!orgsVisible);
              setTopicsVisible(false);
            }}
            onClose={() => setOrgsVisible(false)}
            filteredContent={filteredOrganizations}
            itemOnPress={(item) => toggleOrganization(item)}
          />
        </View>
        <View className="flex-row items-center mt-10">
          {showStatus && (
            <View className="flex-row items-center self-center">
              <Text className="text-input text-neutral-950 dark:text-neutral-50 font-[sans-600] mr-16">
                {t('admin:search:status')}:
              </Text>

              <RadioGroup
                radioButtons={Object.values(HelpdeskIssueStatusFilter).map((status) => ({
                  id: status,
                  label: t(`admin:search:${status}`),
                  value: status,
                }))}
                selectedId={selectedStatus}
                onPress={setSelectedStatus}
                layout={'row'}
              />
            </View>
          )}

          <View
            className="justify-start flex-col ml-auto"
            style={{
              width: isWidthBelowMedium ? '100%' : '45%',
            }}
          >
            <ResultCounter
              pagination={pagination}
              totalCount={totalCount}
              classNames="mb-0 self-end"
            />
            {(params?.organizations?.length >= 1 ||
              params?.topics?.length >= 1 ||
              !!search ||
              (showStatus && selectedStatus !== HelpdeskIssueStatusFilter.all)) && (
              <Pressable
                className="justify-center items-center self-end"
                onPress={emptyFilters}
                style={{
                  alignSelf: 'flex-end',
                  marginBottom: 0,
                }}
              >
                <Text className="text-primary-600 dark:text-primary-500 text-body font-[sans-400] py-2 whitespace-nowrap">
                  {t('articles:remove_search')}
                </Text>
              </Pressable>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

export default HelpdeskIssueFilter;
